import { FC, SVGProps } from 'react';

import { GioChannelSubtype } from '@scaleaq/sitecontrol-lib';
import { icon } from '@scaleaq/sui-tokens/colors/icon';

import { Icon } from './scaleUi';

type GetIconByChannelSubType = ({
  gioChannelSubtype,
  isOn,
}: {
  gioChannelSubtype?: GioChannelSubtype;
  isOn?: boolean;
}) => FC<SVGProps<SVGSVGElement>> | undefined;

/**
 * Returns the applicable icon for the GIO channel subtype.
 * Some icons have two states (on/off, open/closed, etc.).
 * `isOn` defaults to false (off/closed).
 */
export const getIconByGioChannelSubType: GetIconByChannelSubType = ({
  gioChannelSubtype,
  isOn = false,
}) => {
  switch (gioChannelSubtype) {
    case GioChannelSubtype.DigitalInputFireAlarm:
      return isOn ? Icon.FireEmergency : Icon.Fire;
    case GioChannelSubtype.DigitalInputBioVacAlarm:
      return isOn ? Icon.GasMaskYes : Icon.GasMaskNo;
    case GioChannelSubtype.DigitalInputDoor:
      return isOn ? Icon.DoorOpen24 : Icon.DoorClosed24;
    case GioChannelSubtype.DigitalInputValve:
      return isOn ? Icon.ValveOpenTwoWayDigital : Icon.ValveClosedTwoWayDigital;
    case GioChannelSubtype.DigitalInputHatch:
      return isOn ? Icon.HatchOpen : Icon.HatchClosed;
    case GioChannelSubtype.DigitalOutputCircuitBreaker:
      return isOn ? Icon.CircuitBreakerOpen : Icon.CircuitBreakerClosed;
    case GioChannelSubtype.DigitalOutputGenericPump:
      return isOn ? Icon.Pump : Icon.Pump;
    case GioChannelSubtype.DigitalOutputSiren:
      return Icon.AlarmBell;
    case GioChannelSubtype.DigitalOutputHazardLight:
      return Icon.AlarmSiren;
    case GioChannelSubtype.DigitalInputGeneric:
    case GioChannelSubtype.DigitalOutputGeneric:
      return Icon.Io;
    default:
      return Icon.Circle;
  }
};

type GetStatusColor = ({
  channelSubtype,
  isOn,
}: {
  channelSubtype?: GioChannelSubtype;
  isOn?: boolean;
}) => string;

/**
 * Returns the applicable status color for the GIO channel subtype.
 * Colors may reflect two states (on/off, open/closed, etc.).
 * `isOn` defaults to false (off/closed).
 */
export const getStatusColorByGioChannelSubtype: GetStatusColor = ({
  channelSubtype,
  isOn = false,
}) => {
  switch (channelSubtype) {
    case GioChannelSubtype.DigitalInputGeneric:
    case GioChannelSubtype.DigitalOutputGeneric:
      return isOn ? icon.success[100] : icon.neutral.contrast;
    case GioChannelSubtype.DigitalInputBioVacAlarm:
    case GioChannelSubtype.DigitalOutputHazardLight:
    case GioChannelSubtype.DigitalOutputSiren:
    case GioChannelSubtype.DigitalInputFireAlarm:
      return isOn ? icon.critical[100] : icon.neutral.contrast;
    case GioChannelSubtype.DigitalInputDoor:
    case GioChannelSubtype.DigitalInputHatch:
    case GioChannelSubtype.DigitalInputValve:
    case GioChannelSubtype.DigitalOutputCircuitBreaker:
    case GioChannelSubtype.DigitalOutputGenericPump:
      return isOn ? icon.warning[100] : icon.neutral.contrast;
    default:
      return icon.neutral.contrast;
  }
};
